import React, { useEffect, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Calendar, RefreshCw, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';

export default function News() {
  useSEO('news');
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error

  const load = () => {
    setStatus('loading');
    api.listNews()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setItems(list);
        setStatus(list.length ? 'ready' : 'empty');
      })
      .catch(() => { setItems([]); setStatus('error'); });
  };

  useEffect(() => { load(); }, []);

  const [first, ...rest] = items;

  return (
    <EditRegion label="Duyurular" to="/admin/news">
      <>
      {/* Hero */}
      <section className="relative bg-ink-600 text-cream overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #C9A96E 0, transparent 55%), radial-gradient(circle at 80% 70%, #E8826F 0, transparent 55%)' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <InlineText settingKey="news_hero_kicker" fallback="Duyurular" as="div"
            className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold" />
          <InlineText settingKey="news_hero_title" fallback={'Vakıftan <span class="italic text-coral-200">güncel</span> haberler.'} as="h1"
            html multiline className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] mt-5 max-w-4xl" />
          <InlineText settingKey="news_hero_text" fallback="YEKÜV'ün son projeleri, etkinlikleri, kampanyaları ve duyuruları." as="p"
            multiline className="mt-6 text-cream/70 max-w-2xl" />
        </div>
      </section>

      <section className="bg-cream min-h-[400px]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
          {/* Loading skeleton */}
          {status === 'loading' && (
            <div data-testid="news-loading">
              <div className="grid lg:grid-cols-12 gap-8 items-center mb-16">
                <div className="lg:col-span-7 rounded-3xl aspect-[16/10] bg-ink-50 animate-pulse" />
                <div className="lg:col-span-5 space-y-4">
                  <div className="h-3 w-32 bg-ink-50 rounded-full animate-pulse" />
                  <div className="h-12 w-full bg-ink-50 rounded-2xl animate-pulse" />
                  <div className="h-12 w-3/4 bg-ink-50 rounded-2xl animate-pulse" />
                  <div className="h-4 w-full bg-ink-50 rounded-full animate-pulse" />
                  <div className="h-4 w-5/6 bg-ink-50 rounded-full animate-pulse" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1,2,3].map(i => (
                  <div key={i} className="bg-white rounded-3xl border border-ink-100 overflow-hidden">
                    <div className="aspect-[4/3] bg-ink-50 animate-pulse" />
                    <div className="p-6 space-y-3">
                      <div className="h-3 w-24 bg-ink-50 rounded-full animate-pulse" />
                      <div className="h-6 w-full bg-ink-50 rounded-xl animate-pulse" />
                      <div className="h-6 w-2/3 bg-ink-50 rounded-xl animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error state */}
          {status === 'error' && (
            <div className="py-20 text-center" data-testid="news-error">
              <div className="w-16 h-16 rounded-2xl bg-coral-50 text-coral-300 grid place-items-center mx-auto">
                <AlertCircle className="w-7 h-7" />
              </div>
              <h2 className="font-display text-3xl text-ink-600 mt-6">Duyurular yüklenemedi</h2>
              <p className="mt-3 text-ink-500 max-w-md mx-auto">Sunucuya bağlanırken bir sorun oluştu. Lütfen birkaç saniye sonra tekrar deneyin.</p>
              <button
                onClick={load}
                data-testid="news-retry"
                className="mt-6 inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-5 h-12 font-semibold hover:bg-ink-500 transition-colors"
              >
                <RefreshCw className="w-4 h-4" /> Tekrar dene
              </button>
            </div>
          )}

          {/* Empty state */}
          {status === 'empty' && (
            <div className="py-20 text-center" data-testid="news-empty">
              <div className="w-16 h-16 rounded-2xl bg-cream-200 text-ink-400 grid place-items-center mx-auto">
                <Calendar className="w-7 h-7" />
              </div>
              <h2 className="font-display text-3xl text-ink-600 mt-6">Henüz duyuru yok</h2>
              <p className="mt-3 text-ink-500 max-w-md mx-auto">Yeni duyurular eklendiğinde burada listelenecek. Bültenimize abone olarak güncel kalın.</p>
              <Link to="/" className="mt-6 inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-5 h-12 font-semibold hover:bg-ink-500 transition-colors">
                Anasayfaya dön <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* Ready state */}
          {status === 'ready' && first && (
            <>
              <Link to={`/duyurular/${first.slug}`} className="group grid lg:grid-cols-12 gap-8 items-center mb-16" data-testid="news-featured">
                <div className="lg:col-span-7 rounded-3xl overflow-hidden aspect-[16/10] bg-ink-50">
                  <img src={first.image} alt={first.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
                    <span className="bg-gold text-ink-700 rounded-full px-2.5 py-1 font-semibold">Öne Çıkan</span>
                    <span className="text-ink-400 inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {first.date}</span>
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.02] text-ink-600 group-hover:text-ink-500">{first.title}</h2>
                  <p className="mt-4 text-ink-500 leading-relaxed">{first.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-ink-600 font-medium">Devamını oku <ArrowUpRight className="w-4 h-4" /></span>
                </div>
              </Link>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-grid">
                {rest.map(n => (
                  <Link key={n.id} to={`/duyurular/${n.slug}`} className="group bg-white rounded-3xl border border-ink-100 overflow-hidden hover:shadow-xl hover:border-gold transition-all">
                    <div className="aspect-[4/3] overflow-hidden bg-ink-50">
                      <img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
                        <span className="text-coral-300 font-semibold">{n.category}</span>
                        <span className="text-ink-400">{n.date}</span>
                      </div>
                      <h3 className="font-display text-xl mt-3 text-ink-600 leading-tight">{n.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
    </EditRegion>
  );
}
